import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { useStore } from "react-redux";
import { flushPendingSaves, leavePendingSaves, type PendingSaves } from "../../../common/input/pendingSaves";
import { useAppDispatch } from "../../../common/redux/hooks";
import type { RootState } from "../../../common/redux/store";
import { editContractThunk } from "../../../common/redux/thunks/contract/edit-contract-thunk";
import { holdsUnsentValues, recheckHeldValues } from "./heldExtensionValues";

export type LeftFrom = "next" | "stepBar";

export type SaveBeforeLeaving = {
  leave: (go: () => void, from?: LeftFrom) => Promise<void>;
  isSaving: boolean;
  saveFailed: boolean;
  heldFrom: LeftFrom | null;
  valueRefused: boolean;
  optionsDecidedElsewhere: string[];
};

const SaveBeforeLeavingContext = createContext<SaveBeforeLeaving | null>(null);

export const useSaveBeforeLeavingState = (registry: PendingSaves): SaveBeforeLeaving => {
  const store = useStore<RootState>();
  const dispatch = useAppDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [heldFrom, setHeldFrom] = useState<LeftFrom | null>(null);
  const [valueRefused, setValueRefused] = useState(false);
  const [optionsDecidedElsewhere, setOptionsDecidedElsewhere] = useState<string[]>([]);

  const leave = useCallback(
    async (go: () => void, from: LeftFrom = "next") => {
      setIsSaving(true);
      setSaveFailed(false);
      setHeldFrom(null);
      setValueRefused(false);
      setOptionsDecidedElsewhere([]);
      const decidedBefore = store.getState().contract.state?.optionsDecidedElsewhere ?? [];
      let saved = true;
      try {
        await flushPendingSaves(registry);
        for (const failed of store.getState().contract.state?.failedEdits ?? []) {
          await dispatch(editContractThunk({ command: failed.command }));
        }
        await recheckHeldValues(registry, store.getState().contract.state?.contract);
      } catch {
        saved = false;
      } finally {
        setIsSaving(false);
      }
      const state = store.getState().contract.state;
      const newlyDecided = (state?.optionsDecidedElsewhere ?? []).filter((id) => !decidedBefore.includes(id));
      setOptionsDecidedElsewhere(newlyDecided);
      const holdsValues = holdsUnsentValues(registry);
      if (saved && ((state?.errors.length ?? 0) > 0 || holdsValues)) saved = false;
      if (saved && newlyDecided.length === 0) {
        void leavePendingSaves(registry);
        go();
        return;
      }
      setHeldFrom(from);
      if (!saved) {
        setSaveFailed(true);
        setValueRefused(holdsValues || (state?.failedEdits ?? []).some((failed) => failed.valueRefused));
      }
    },
    [registry, store, dispatch],
  );

  return {
    leave,
    isSaving,
    saveFailed,
    heldFrom,
    valueRefused,
    optionsDecidedElsewhere,
  };
};

export const SaveBeforeLeavingProvider = ({ value, children }: { value: SaveBeforeLeaving; children: ReactNode }) => (
  <SaveBeforeLeavingContext.Provider value={value}>{children}</SaveBeforeLeavingContext.Provider>
);

export const useSaveBeforeLeaving = (): SaveBeforeLeaving | null => useContext(SaveBeforeLeavingContext);
